import { Story } from "inkjs";
import type { DialogueTone, InkStep } from "../types/narrative.js";

/**
 * Fetches a compiled Ink JSON export and returns a live inkjs Story instance.
 * The caller owns the Story and advances it by calling step().
 */
export async function loadInkStory(url: string): Promise<Story> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to load Ink story: ${url} (${response.status})`);
  }
  const json: unknown = await response.json();
  return new Story(JSON.stringify(json));
}

/**
 * Advances the story to the next block of content.
 * Returns the text and available choices for the current beat.
 */
/** Parses a `# tone: <value>` tag from a flat tags array. Returns "neutral" if absent. */
function parseTone(tags: string[] | null): DialogueTone {
  if (!tags) return "neutral";
  for (const tag of tags) {
    const match = /^tone\s*:\s*(\w+)$/i.exec(tag.trim());
    if (match) {
      const val = match[1]!.toLowerCase();
      if (val === "warm" || val === "hostile") return val;
    }
  }
  return "neutral";
}

export function stepStory(story: Story): InkStep {
  let text = "";
  const allTags: string[] = [];

  while (story.canContinue) {
    text += story.Continue() ?? "";
    const tags = story.currentTags;
    if (tags) allTags.push(...tags);
  }

  const choices = story.currentChoices.map((c, i) => ({ index: i, text: c.text }));

  return {
    text: text.trim(),
    choices,
    ended: !story.canContinue && choices.length === 0,
    tone: parseTone(allTags),
  };
}
